/**
 * useQuickStatsCore — Platform-agnostic quick stats data fetching hook
 *
 * Handles search, player stats, team stats, and team H2H fetching.
 * Accepts platform-specific `fetchFn` and `buildUrl` to work on both web and mobile.
 *
 * Web: passes `authFetch` from AuthContext + `apiUrl` from config
 * Mobile: passes `authFetch` from authStore + identity function for URL
 */

import { useCallback, useState } from 'react';
import { isLeagueKey, type LeagueKey } from '../league';
import type { GameData, PreseasonWindow, StatAverages, TeamGameData } from '../statsChart';

// =============================================================================
// Types
// =============================================================================

export interface QuickStatsPlayerResult {
  type: 'player';
  id: number;
  name: string;
  team: string | undefined;
  teamAbbr: string | undefined;
  position: string | undefined;
  leagueKey: LeagueKey;
}

export interface QuickStatsTeamResult {
  type: 'team';
  name: string;
  abbr: string;
  leagueKey: LeagueKey;
}

export type QuickStatsSearchResult = QuickStatsPlayerResult | QuickStatsTeamResult;

/**
 * Saved recent searches that name a league. Ones saved before results carried a league are
 * dropped rather than shown in a guessed one.
 */
export function recentSearchesWithLeague<T extends { leagueKey?: string }>(
  items: readonly T[],
): Array<T & { leagueKey: LeagueKey }> {
  return items.filter(
    (item): item is T & { leagueKey: LeagueKey } =>
      typeof item.leagueKey === 'string' && isLeagueKey(item.leagueKey),
  );
}

/** Player stats response from /api/stats/players/:name */
export interface QuickPlayerStatsData {
  playerId?: number;
  playerName: string;
  team: string | null;
  position?: string | null;
  recentGames: GameData[];
  seasonAverages?: StatAverages | null;
  l5?: StatAverages | null;
  l10?: StatAverages | null;
  l20?: StatAverages | null;
  h2hGames?: GameData[];
  opponentAbbr?: string;
  /** Present only in the NBA preseason window. */
  preseasonWindow?: PreseasonWindow;
}

/** Team stats response from /api/stats/teams/:name */
export interface QuickTeamStatsData {
  teamId?: number;
  teamName: string;
  record: string;
  homeRecord?: string;
  awayRecord?: string;
  l10Record?: string;
  ppg?: number;
  oppPpg?: number;
  recentGames: TeamGameData[];
}

/** Team H2H response from /api/teams/h2h */
interface QuickTeamH2HTeamStats {
  teamName: string;
  wins: number;
  losses: number;
  homeWins: number;
  homeLosses: number;
  awayWins: number;
  awayLosses: number;
  l10Wins: number;
  l10Losses: number;
  ppg: number;
  oppPpg: number;
  recentGames: TeamGameData[];
}

export interface QuickTeamH2HData {
  team1Stats: QuickTeamH2HTeamStats;
  team2Stats: QuickTeamH2HTeamStats;
  h2hHistory: TeamGameData[];
  h2hRecord: {
    team1Wins: number;
    team2Wins: number;
    lastMeeting: string | null;
  };
}

interface SearchResponse {
  results: QuickStatsSearchResult[];
}

export interface UseQuickStatsCoreResult {
  searchPlayersAndTeams: (
    query: string,
    leagueKeys?: LeagueKey[],
  ) => Promise<QuickStatsSearchResult[]>;
  fetchPlayerStats: (
    playerName: string,
    opponent: string | undefined,
    leagueKey: LeagueKey,
  ) => Promise<QuickPlayerStatsData>;
  fetchTeamStats: (teamName: string, leagueKey: LeagueKey) => Promise<QuickTeamStatsData>;
  fetchTeamH2H: (team1: string, team2: string, leagueKey: LeagueKey) => Promise<QuickTeamH2HData>;
  isSearching: boolean;
  isFetchingPlayer: boolean;
  isFetchingTeam: boolean;
  isFetchingH2H: boolean;
  error: string | null;
  clearError: () => void;
}

interface QuickStatsCoreOptions {
  /** Authenticated fetch function (platform-specific) */
  fetchFn: (url: string) => Promise<Response>;
  /** URL builder — web prefixes with API_BASE_URL, mobile uses identity */
  buildUrl?: (path: string) => string;
}

// =============================================================================
// Hook
// =============================================================================

export function useQuickStatsCore({
  fetchFn,
  buildUrl = (p) => p,
}: QuickStatsCoreOptions): UseQuickStatsCoreResult {
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingPlayer, setIsFetchingPlayer] = useState(false);
  const [isFetchingTeam, setIsFetchingTeam] = useState(false);
  const [isFetchingH2H, setIsFetchingH2H] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const searchPlayersAndTeams = useCallback(
    async (query: string, leagueKeys?: LeagueKey[]): Promise<QuickStatsSearchResult[]> => {
      if (query.length < 2) return [];

      setIsSearching(true);
      setError(null);

      try {
        let url = buildUrl(`/api/stats/search?q=${encodeURIComponent(query)}`);
        if (leagueKeys?.length) url += `&leagueKey=${encodeURIComponent(leagueKeys.join(','))}`;
        const res = await fetchFn(url);
        if (!res.ok) throw new Error('Search failed');
        const data: SearchResponse = await res.json();
        return data.results || [];
      } catch (err) {
        setError('Search failed. Please try again.');
        throw err;
      } finally {
        setIsSearching(false);
      }
    },
    [fetchFn, buildUrl],
  );

  const fetchPlayerStats = useCallback(
    async (
      playerName: string,
      opponent: string | undefined,
      leagueKey: LeagueKey,
    ): Promise<QuickPlayerStatsData> => {
      setIsFetchingPlayer(true);
      setError(null);

      try {
        const params = new URLSearchParams({ leagueKey });
        if (opponent) params.set('opponent', opponent);
        const url = buildUrl(
          `/api/stats/players/${encodeURIComponent(playerName)}?${params.toString()}`,
        );
        const res = await fetchFn(url);
        if (!res.ok) throw new Error('Failed to fetch player stats');
        return (await res.json()) as QuickPlayerStatsData;
      } catch (err) {
        setError('Failed to load player stats. Please try again.');
        throw err;
      } finally {
        setIsFetchingPlayer(false);
      }
    },
    [fetchFn, buildUrl],
  );

  const fetchTeamStats = useCallback(
    async (teamName: string, leagueKey: LeagueKey): Promise<QuickTeamStatsData> => {
      setIsFetchingTeam(true);
      setError(null);

      try {
        const params = new URLSearchParams({ leagueKey });
        const url = buildUrl(
          `/api/stats/teams/${encodeURIComponent(teamName)}?${params.toString()}`,
        );
        const res = await fetchFn(url);
        if (!res.ok) throw new Error('Failed to fetch team stats');
        return (await res.json()) as QuickTeamStatsData;
      } catch (err) {
        setError('Failed to load team stats. Please try again.');
        throw err;
      } finally {
        setIsFetchingTeam(false);
      }
    },
    [fetchFn, buildUrl],
  );

  const fetchTeamH2H = useCallback(
    async (team1: string, team2: string, leagueKey: LeagueKey): Promise<QuickTeamH2HData> => {
      setIsFetchingH2H(true);
      setError(null);

      try {
        const params = new URLSearchParams({ team1, team2, leagueKey });
        const url = buildUrl(`/api/teams/h2h?${params.toString()}`);
        const res = await fetchFn(url);
        if (!res.ok) throw new Error('Failed to fetch team H2H data');
        return (await res.json()) as QuickTeamH2HData;
      } catch (err) {
        setError('Failed to load team H2H data. Please try again.');
        throw err;
      } finally {
        setIsFetchingH2H(false);
      }
    },
    [fetchFn, buildUrl],
  );

  return {
    searchPlayersAndTeams,
    fetchPlayerStats,
    fetchTeamStats,
    fetchTeamH2H,
    isSearching,
    isFetchingPlayer,
    isFetchingTeam,
    isFetchingH2H,
    error,
    clearError,
  };
}
